import React, { ExoticComponent, Fragment, ReactNode } from 'react';
import { Form as FormikForm } from 'formik';
import { FormattedMessage } from 'react-intl';
import Alert from 'react-bootstrap/Alert';
import { TextField } from '../components/fields';
import SubmitButton from '../components/submit-button';
import ErrorMessage from '../components/error-message';
import {
  AddNewTemplateName,
  CreateNewTemplateOnSave,
  EditTemplateOnSave,
  AddNewTemplateRestrictedEditing,
  AddNewTemplateDisableWordExport,
  AddNewClauseName,
  CreateNewClauseOnSave,
  EditClauseOnSave,
} from '../consts/forms-constants';
import { TemplateType } from '../consts/forms-types';
import { DocumentSourceEnum } from '../components/document-wizard/document-editor/constants/EditorConstats';
import { FolderTreeSelector, TemplateFolder } from '../components/folder-tree-selector';
import {
  FormSection,
  FormField,
  ToggleField
} from '../components/shared/form-components';
import styles from '../components/shared/form-components/form-components.module.scss';

interface AddNewTemplateFormProps {
  bodyContainer: ExoticComponent<{ children?: ReactNode }>;
  actionsContainer: ExoticComponent<{ children?: ReactNode }>;
  renderSecondaryAction: any;
  errors: any;
  isSubmitting: boolean;
  isValid: boolean;
  templateFormEditMode: boolean;
  canConfigurePublishingSettings?: boolean;
  isRestrictedEditingEnabled: boolean;
  handleRestrictedEditingChange: () => void;
  isDisableWordExportEnabled: boolean;
  handleDisableWordExportChange: () => void;
  templateType?: TemplateType | null;
  templateSource?: DocumentSourceEnum;
  folders?: TemplateFolder[];
  selectedFolderId?: string | null;
  onFolderSelectionChange?: (folderId: string | null) => void;
  foldersLoading?: boolean;
}

const AddNewTemplateForm = ({
  bodyContainer: BodyContainer = Fragment,
  actionsContainer: ActionsContainer = Fragment,
  renderSecondaryAction,
  errors,
  isSubmitting,
  isValid,
  templateFormEditMode,
  canConfigurePublishingSettings = true,
  isRestrictedEditingEnabled,
  handleRestrictedEditingChange,
  isDisableWordExportEnabled,
  handleDisableWordExportChange,
  templateType,
  templateSource,
  folders = [],
  selectedFolderId = null,
  onFolderSelectionChange,
  foldersLoading = false,
}: AddNewTemplateFormProps) => {
  const newTemplateName = templateType === TemplateType.CLAUSE ? AddNewClauseName : AddNewTemplateName;
  const createButtonText = templateType === TemplateType.CLAUSE ? CreateNewClauseOnSave : CreateNewTemplateOnSave;
  const editButtonText = templateType === TemplateType.CLAUSE ? EditClauseOnSave : EditTemplateOnSave;
  
  return (
    <FormikForm noValidate>
      <div className={styles.modalFormBody}>
        <BodyContainer>
          {errors._general && (
            <Alert variant="danger">
              <ErrorMessage>{errors._general}</ErrorMessage>
            </Alert>
          )}

          <FormSection>
            <TextField
              id="addNewTemplate.name"
              name="template_name"
              type="template_name"
              label={<FormattedMessage id={newTemplateName.id} defaultMessage={newTemplateName.defaultMessage} />}
            />
          </FormSection>

          {canConfigurePublishingSettings &&
            templateType === TemplateType.DOCUMENT &&
            templateSource !== DocumentSourceEnum.IMPORTED && (
            <>
              <FormSection>
                <ToggleField
                  messageId={AddNewTemplateRestrictedEditing.id}
                  defaultMessage={AddNewTemplateRestrictedEditing.defaultMessage}
                  tooltip={AddNewTemplateRestrictedEditing.TOOLTIP_MESSAGE}
                  tooltipTestId="add-form-restricted-editing-tooltip"
                  checked={isRestrictedEditingEnabled}
                  onChange={handleRestrictedEditingChange}
                  disabled={templateFormEditMode}
                />
              </FormSection>
              
              <FormSection>
                <ToggleField
                  messageId={AddNewTemplateDisableWordExport.id}
                  defaultMessage={AddNewTemplateDisableWordExport.defaultMessage}
                  tooltip={AddNewTemplateDisableWordExport.TOOLTIP_MESSAGE}
                  tooltipTestId="add-form-disable-word-export-tooltip"
                  checked={isDisableWordExportEnabled}
                  onChange={handleDisableWordExportChange}
                />
              </FormSection>
            </>
          )}

          {onFolderSelectionChange && (
            <FormSection>
              <FormField
                label={templateType === TemplateType.CLAUSE ? 'Clause Folder' : 'Template Folder'}
                messageId={templateType === TemplateType.CLAUSE ? 'label.clause-folder' : 'label.template-folder'}
                defaultMessage={templateType === TemplateType.CLAUSE ? 'Clause Folder' : 'Template Folder'}
                helpText={
                  templateType === TemplateType.CLAUSE
                    ? "Choose the folder where this clause will be created. Select 'All Clauses (Root)' to create it at the top level."
                    : "Choose the folder where this template will be created. Select 'All Templates (Root)' to create it at the top level."
                }
              >
                {foldersLoading ? (
                  <div className="text-muted">
                    <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                  </div>
                ) : (
                  <FolderTreeSelector
                    folders={folders}
                    selectedFolderId={selectedFolderId}
                    onSelectionChange={onFolderSelectionChange}
                    disabled={isSubmitting}
                    context={templateType === TemplateType.CLAUSE ? 'clauses' : 'templates'}
                  />
                )}
              </FormField>
            </FormSection>
          )}
        </BodyContainer>
      </div>
      
      <div className={styles.modalFormFooter}>
        <ActionsContainer>
          {renderSecondaryAction && renderSecondaryAction()}
          <SubmitButton
            label={
              templateFormEditMode ? (
                <FormattedMessage id={editButtonText.id} defaultMessage={editButtonText.defaultMessage} />
              ) : (
                <FormattedMessage id={createButtonText.id} defaultMessage={createButtonText.defaultMessage} />
              )
            }
            disabled={isSubmitting || !isValid}
            isSubmitting={isSubmitting}
          />
        </ActionsContainer>
      </div>
    </FormikForm>
  );
};

export default AddNewTemplateForm;